import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { FaArrowLeft, FaCheckCircle, FaClock, FaTable, FaFilter, FaFileInvoice } from 'react-icons/fa';
import { ordersAPI } from '../utils/api';
import './OrderManagement.css';

function OrderManagement() {
  const [orders, setOrders] = useState([]);
  const [viewMode, setViewMode] = useState('pending'); // 'pending', 'all', 'confirmed', 'completed'
  const [notification, setNotification] = useState(null);
  const [expandedGroupId, setExpandedGroupId] = useState(null);
  const [busyGroupId, setBusyGroupId] = useState(null);
  const [generatingBillForOrder, setGeneratingBillForOrder] = useState(null);

  useEffect(() => {
    loadOrders();
    const interval = setInterval(loadOrders, 3000); // Refresh every 3 seconds
    return () => clearInterval(interval);
  }, [viewMode]);

  const loadOrders = async () => {
    try {
      let response;
      if (viewMode === 'pending') {
        response = await ordersAPI.getPending();
      } else if (viewMode === 'all') {
        response = await ordersAPI.getAll();
      } else {
        response = await ordersAPI.getAll(viewMode);
      }
      setOrders(response.data.orders || []);
    } catch (error) {
      console.error('Error loading orders:', error);
    }
  };

  const handleConfirmOrder = async (orderId) => {
    try {
      await ordersAPI.updateStatus(orderId, 'confirmed');
      await loadOrders();
    } catch (error) {
      console.error('Error confirming order:', error);
      setNotification({
        type: 'error',
        message: error.message || 'Failed to confirm order. Please try again.',
      });
    }
  };

  const handleCompleteOrder = async (orderId) => {
    try {
      await ordersAPI.updateStatus(orderId, 'completed');
      await loadOrders();
    } catch (error) {
      console.error('Error completing order:', error);
      setNotification({
        type: 'error',
        message: error.message || 'Failed to complete order. Please try again.',
      });
    }
  };

  // Group orders by customer (phone + table) and 1-hour continuous window
  const buildGroups = () => {
    if (!orders || orders.length === 0) return [];

    // Filter by view mode: for "pending", show groups with at least one pending order
    const filtered = orders.filter((order) => {
      if (viewMode === 'pending') return order.status === 'pending';
      return true; // 'all' and other modes show everything for now
    });

    if (filtered.length === 0) return [];

    // Sort by created_at ascending for stable grouping
    const sorted = [...filtered].sort(
      (a, b) => new Date(a.created_at).getTime() - new Date(b.created_at).getTime(),
    );

    // First group by customer (mobile + table_number)
    const byCustomer = {};
    for (const order of sorted) {
      const mobile = order.mobile || 'unknown';
      const table = order.table_number != null ? String(order.table_number) : 'no-table';
      const key = `${mobile}::${table}`;
      if (!byCustomer[key]) byCustomer[key] = [];
      byCustomer[key].push(order);
    }

    const groups = [];
    const ONE_HOUR_MS = 60 * 60 * 1000;

    Object.values(byCustomer).forEach((customerOrders) => {
      // Within each customer, split into sessions where orders are within 1 hour window
      let currentGroup = null;

      customerOrders.forEach((order) => {
        const orderTime = new Date(order.created_at).getTime();

        if (!currentGroup) {
          currentGroup = {
            id: `${order.mobile || 'unknown'}-${order.table_number || 'no-table'}-${
              order.id
            }`,
            customerName: order.customer_name,
            mobile: order.mobile,
            tableNumber: order.table_number,
            startTime: order.created_at,
            endTime: order.created_at,
            orders: [order],
          };
          return;
        }

        const firstTime = new Date(currentGroup.startTime).getTime();
        // If within 1 hour of the first order in the group, keep grouping
        if (orderTime - firstTime <= ONE_HOUR_MS) {
          currentGroup.orders.push(order);
          currentGroup.endTime = order.created_at;
        } else {
          groups.push(currentGroup);
          currentGroup = {
            id: `${order.mobile || 'unknown'}-${order.table_number || 'no-table'}-${
              order.id
            }`,
            customerName: order.customer_name,
            mobile: order.mobile,
            tableNumber: order.table_number,
            startTime: order.created_at,
            endTime: order.created_at,
            orders: [order],
          };
        }
      });

      if (currentGroup) {
        groups.push(currentGroup);
      }
    });

    // Sort groups by table number then start time
    return groups.sort((a, b) => {
      const tableA = a.tableNumber || 0;
      const tableB = b.tableNumber || 0;
      if (tableA !== tableB) return tableA - tableB;
      return new Date(a.startTime).getTime() - new Date(b.startTime).getTime();
    });
  };

  const groups = buildGroups();

  const toggleGroup = (groupId) => {
    setExpandedGroupId((prev) => (prev === groupId ? null : groupId));
  };

  const handleConfirmGroup = async (group) => {
    try {
      setBusyGroupId(group.id);
      const pendingOrders = group.orders.filter((o) => o.status === 'pending');
      await Promise.all(pendingOrders.map((o) => ordersAPI.updateStatus(o.id, 'confirmed')));
      await loadOrders();
    } catch (error) {
      console.error('Error confirming group:', error);
      setNotification({
        type: 'error',
        message: error.message || 'Failed to confirm group. Please try again.',
      });
    } finally {
      setBusyGroupId(null);
    }
  };

  const handleGenerateBillForGroup = async (group) => {
    try {
      setBusyGroupId(group.id);
      // Ensure all orders are at least confirmed
      const pendingOrders = group.orders.filter((o) => o.status === 'pending');
      if (pendingOrders.length > 0) {
        await Promise.all(pendingOrders.map((o) => ordersAPI.updateStatus(o.id, 'confirmed')));
      }

      const orderIds = group.orders.map((o) => o.id);
      const response = await ordersAPI.generateBill(orderIds);
      const bill = response.data?.bill;

      setNotification({
        type: 'success',
        message: bill
          ? `Bill ${bill.bill_number} generated successfully. Total: ₹${bill.total}`
          : 'Bill generated successfully.',
      });

      await loadOrders();
    } catch (error) {
      console.error('Error generating bill for group:', error);
      setNotification({
        type: 'error',
        message: error.message || 'Failed to generate bill. Please try again.',
      });
    } finally {
      setBusyGroupId(null);
    }
  };

  const handleGenerateBillForOrder = async (order) => {
    try {
      setGeneratingBillForOrder(order.id);
      // Ensure order is confirmed
      if (order.status === 'pending') {
        await ordersAPI.updateStatus(order.id, 'confirmed');
      }

      const response = await ordersAPI.generateBill([order.id]);
      const bill = response.data?.bill;

      setNotification({
        type: 'success',
        message: bill
          ? `Bill ${bill.bill_number} generated successfully. Total: ₹${bill.total}`
          : 'Bill generated successfully.',
      });

      await loadOrders();
    } catch (error) {
      console.error('Error generating bill for order:', error);
      setNotification({
        type: 'error',
        message: error.message || 'Failed to generate bill. Please try again.',
      });
    } finally {
      setGeneratingBillForOrder(null);
    }
  };

  return (
    <div className="order-management">
      {notification && (
        <div className={`admin-notification ${notification.type}`}>
          <span>{notification.message}</span>
          <button
            type="button"
            className="admin-notification-close"
            onClick={() => setNotification(null)}
            aria-label="Close notification"
          >
            ×
          </button>
        </div>
      )}
      <header className="management-header">
        <Link to="/dashboard" className="back-btn">
          <FaArrowLeft /> Back
        </Link>
        <h1 className="management-title">Order Management</h1>
        <div className="filter-controls">
          <button
            className={`filter-btn ${viewMode === 'pending' ? 'active' : ''}`}
            onClick={() => setViewMode('pending')}
          >
            <FaClock /> Pending
          </button>
          <button
            className={`filter-btn ${viewMode === 'all' ? 'active' : ''}`}
            onClick={() => setViewMode('all')}
          >
            <FaFilter /> All Orders
          </button>
        </div>
      </header>

      <div className="management-content">
        {groups.length === 0 ? (
          <div className="no-orders">
            <FaTable className="no-orders-icon" />
            <p>No {viewMode === 'pending' ? 'pending' : ''} orders</p>
          </div>
        ) : (
          <div className="tables-grid">
            {groups.map((group) => {
              const pendingCount = group.orders.filter((o) => o.status === 'pending').length;
              const confirmedCount = group.orders.filter((o) => o.status === 'confirmed').length;
              const completedCount = group.orders.filter((o) => o.status === 'completed').length;

              return (
                <div key={group.id} className="table-card">
                  <div className="table-header" onClick={() => toggleGroup(group.id)}>
                    <FaTable className="table-icon" />
                    <div className="table-header-info">
                      <h2 className="table-number">
                        Table {group.tableNumber || 'N/A'} • {group.mobile || 'No phone'}
                      </h2>
                      <p className="group-subtitle">
                        {group.customerName || 'Customer'} • {group.orders.length} order(s) •{' '}
                        {new Date(group.startTime).toLocaleTimeString()} -{' '}
                        {new Date(group.endTime).toLocaleTimeString()}
                      </p>
                    </div>
                    <span className="order-count">
                      {pendingCount} pending, {confirmedCount} confirmed, {completedCount} completed
                    </span>
                  </div>

                  <div className="group-actions">
                    <button
                      className="complete-btn"
                      onClick={() => handleGenerateBillForGroup(group)}
                      disabled={busyGroupId === group.id || group.orders.length === 0}
                    >
                      Generate Bill
                    </button>
                  </div>

                  {expandedGroupId === group.id && (
                    <div className="table-orders">
                      {group.orders.map((order) => (
                        <div key={order.id} className="order-item">
                          <div className="order-info">
                            <div className="order-dish">
                              <img
                                src={
                                  order.dish_data?.image ||
                                  order.dish?.image ||
                                  'https://via.placeholder.com/50'
                                }
                                alt={order.dish_data?.name || order.dish?.name}
                                className="order-dish-image"
                              />
                              <div className="order-dish-details">
                                <h4 className="order-dish-name">
                                  {order.dish_data?.name || order.dish?.name || 'Unknown'}
                                </h4>
                                <p className="order-customer">
                                  {order.customer_name || order.name} • {order.mobile}
                                </p>
                                <p className="order-qty-price">
                                  Qty: {order.quantity || 1} • ₹
                                  {order.dish_data?.price || order.dish?.price || 0}
                                </p>
                                {order.created_at && (
                                  <p className="order-time">
                                    {new Date(order.created_at).toLocaleTimeString()}
                                  </p>
                                )}
                              </div>
                            </div>
                            <div className="order-status">
                              <span className={`status-badge ${order.status}`}>
                                {order.status}
                              </span>
                            </div>
                          </div>
                          <div className="order-actions">
                            {order.status === 'pending' && (
                              <button
                                className="confirm-btn"
                                onClick={() => handleConfirmOrder(order.id)}
                              >
                                <FaCheckCircle /> Confirm
                              </button>
                            )}
                            {order.status === 'confirmed' && (
                              <>
                                <button
                                  className="generate-bill-btn"
                                  onClick={() => handleGenerateBillForOrder(order)}
                                  disabled={generatingBillForOrder === order.id}
                                >
                                  <FaFileInvoice /> Generate Bill
                                </button>
                                <button
                                  className="complete-btn"
                                  onClick={() => handleCompleteOrder(order.id)}
                                >
                                  Complete
                                </button>
                              </>
                            )}
                            {order.status === 'completed' && (
                              <span className="completed-text">Order Completed</span>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}

export default OrderManagement;


